import React from 'react';

import { formatItineraryInfo, formatShipTitleArticle } from '../../helpers/cruiseSearchHelper';
import { checkColonExistance } from '../../helpers/requestFormHelpers';
import { __ } from '../../helpers/TranslationService';
import { ICabinInfo, ICruiseReducer, IRate } from '../../interfaces';
import { IPageDataReducer } from '../../reducers/pageData';
import { IRequestFormReducer } from '../../interfaces/IRequestForm';
import { AdultsIcon } from '../Icons/AdultsIcon';
import { AirplaneIcon } from '../Icons/AirplaneIcon';
import { CalendarIcon } from '../Icons/CalendarIcon';
import { KeysIcon } from '../Icons/KeysIcon';
import { RequestFormPrice } from './RequestFormPrice';
import { StarIcon } from '../Icons/StarIcon';

interface IRequestFormSummary {
    cruise: ICruiseReducer;
    pageData: IPageDataReducer;
    requestForm: IRequestFormReducer;
    selectedCabinId: number;
    selectedSailIndex: number;
    startHarbour: string;
    cabin: ICabinInfo;
    departure: number;
    isDualCurrency: boolean;
    showDirectBookingLabel: boolean;
    searchUrlSlug: string;
    isAllInclusive: boolean;
    selectedRates?: IRate[];
}

const RequestFormSummary = (props: IRequestFormSummary) => {
    const {
        cruise,
        requestForm,
        selectedCabinId,
        selectedSailIndex,
        startHarbour,
        pageData,
        isAllInclusive,
        selectedRates,
        ...priceProps
    } = props;

    const adultsNum = requestForm.adultsNum;
    const childrenNum = requestForm.childrenNum;

    const getSelectedCabin = () => {
        const index = cruise.cabins.findIndex((item) => item.cabinType.nid === selectedCabinId);
        return index >= 0 ? cruise.cabins[index] : null;
    };

    const selectedCabinObj: ICabinInfo = getSelectedCabin();

    if (!selectedCabinObj) return null;

    const selectedSail = cruise.sails[selectedSailIndex];
    const waypointsHarboursArr = cruise.waypoints?.length
        ? cruise.waypoints.filter((waypoint) => !!waypoint.city).map((item) => item.city)
        : [];
    const uniqueWayPointCities = Array.from(new Set(waypointsHarboursArr));
    const nightsPortsDaysAtSea =
        formatItineraryInfo(cruise.nights, uniqueWayPointCities.length) + ' ';

    const cruisePageTitle = cruise.operator?.isDreamlinesPackage
        ? cruise.title
        : `${cruise.zone} ${__('from', 'dreamlines')} ${startHarbour}`;

    return (
        <div className="reqFormSummaryContainer">
            <div className="reqFormSummaryContainer__destination">
                <span className="reqFormSummaryContainer__destination__nights">
                    {nightsPortsDaysAtSea} {formatShipTitleArticle(cruise.company.title)}{' '}
                    {cruise.ship.title}
                </span>

                <h3>{cruisePageTitle}</h3>
            </div>

            <ul className="reqFormSummaryContainer__briefSummary">
                {selectedCabinObj?.cabinType?.title && (
                    <li>
                        <KeysIcon width={24} height={24} />
                        <span>{selectedCabinObj?.cabinType.title}</span>
                    </li>
                )}
                <li>
                    <AdultsIcon width={24} height={24} />
                    <span>
                        {adultsNum} {__('adults', 'dreamlines')}
                        {', '}
                        {childrenNum} {__('children', 'dreamlines')}
                    </span>
                </li>
                <li>
                    <CalendarIcon width={24} height={24} />
                    <span>{selectedSail?.label}</span>
                </li>

                {cruise.includesFlight && (
                    <li>
                        <AirplaneIcon width={24} height={24} />
                        <span>{__('including flight', 'dreamlines')}</span>
                    </li>
                )}
                {isAllInclusive && (
                    <li>
                        <StarIcon width={24} height={24} />
                        <span>{__('All-inclusive', 'dreamlines')}</span>
                    </li>
                )}
            </ul>

            <div className="reqFormSummaryContainer__priceInfo">
                <RequestFormPrice
                    company={cruise.company.title}
                    ship={cruise.ship.title}
                    exchangeRate={pageData.header.exchangeRate}
                    discountThreshold={pageData.discountThreshold}
                    countryCode={pageData.appConfig.countryCode}
                    phone={pageData.footer.contactForm?.phoneLink}
                    email={pageData.footer.contactForm?.emailLink}
                    selectedAirportCode={requestForm.selectedAirportCode}
                    selectedFlightCode={requestForm.selectedFlightClass}
                    isAllInclusive={isAllInclusive}
                    selectedRates={selectedRates}
                    market={pageData.brandConfig.portalId}
                    featureFlags={pageData.featureFlags}
                    brandConfig={pageData.brandConfig}
                    {...priceProps}
                />
            </div>
        </div>
    );
};

export default RequestFormSummary;
